import * as React from 'react';
import { DispatchWithoutAction } from 'react';
import { PassengersById, SegmentsById } from '../TransferService/model';
import ModalX from '../../../BaseComponents/ModalX';
import { Money } from '../../../Money';
import { GdsService_OrderAdditionalServiceGdsServiceVip_Fragment, Money as MoneyType } from '@websky/graphql';
import Button from '../../../BaseComponents/Button';
import Checkbox from '../../../BaseComponents/Checkbox';
import { State } from '../TransferSelector/model';
import { PassengerId, SegmentId } from '../../../types';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import { FooterMobile } from '@shared/ui';

interface Props {
	service: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;
	servicesState: State;
	passengers: PassengersById;
	segments: SegmentsById;
	isOpen: boolean;
	isDirty: boolean;
	onClose: DispatchWithoutAction;
	onClear: () => void;
	onChange: (passengerId: PassengerId, segmentId: SegmentId) => void;
	totalPrice: MoneyType;
	onSubmit: DispatchWithoutAction;
}

const TransferSelectorModal: React.FC<Props> = ({
	service,
	passengers,
	segments,
	isOpen,
	isDirty,
	onClose,
	servicesState,
	onClear,
	onChange,
	totalPrice,
	onSubmit
}) => {
	const { t } = useTranslation('Transfer');
	const { TransferSelectorModal: css } = useTheme('Transfer');

	const Header: React.FunctionComponent = () => {
		return (
			<div>
				<div className={css.serviceName}>{service.name}</div>
				<div className={css.serviceDescription}>{service.description}</div>
			</div>
		);
	};

	const Footer: React.FC = () => {
		return (
			<FooterMobile
				className={css.footer}
				left={
					<div className={css.total}>
						<span className={css.price}>
							{t('Total')}:
							<Money money={totalPrice} />
						</span>
						<Divider className={css.divider} orientation="vertical" />
						<div className={css.clear}>
							<span onClick={onClear}>{t('Clear')}</span>
						</div>
					</div>
				}
				right={
					<div className={css.footerControls}>
						<Button variant="secondary" onClick={onClose} classes={{ root: css.cancel }}>
							{t('Cancel')}
						</Button>
						<Button variant="action" onClick={onSubmit} classes={{ root: css.confirm }} disabled={!isDirty}>
							{t('Confirm')}
						</Button>
					</div>
				}
			/>
		);
	};

	return (
		<ModalX
			classes={{ root: css.modalX__root, paper: css.paper }}
			isOpen={isOpen}
			onClose={onClose}
			title={<Header />}
			footer={<Footer />}
		>
			<div className={css.content}>
				{service.allowedSegments.map((segmentGroup, index) => {
					return (
						<React.Fragment key={index}>
							{segmentGroup.map(segmentId => {
								return (
									<div key={segmentId}>
										<div className={css.heading}>
											<div className={css.date}>{segments[segmentId].date}</div>
											<div className={css.city}>{segments[segmentId].city}</div>
											<div className={css.cost}>{t('Cost')}</div>
										</div>

										{service.allowedPassengers.map((passengerGroup, index) => {
											return passengerGroup.map(passengerId => {
												const passengerService = servicesState[passengerId][segmentId];

												if (!passengerService) {
													return null;
												}

												const disabled = passengerService.confirmed;

												return (
													<div
														className={css.passenger}
														key={passengerId}
														onClick={() =>
															disabled ? null : onChange(passengerId, segmentId)
														}
													>
														<Checkbox
															className={css.checkbox}
															disabled={disabled}
															checked={
																passengerService.selected || passengerService.confirmed
															}
														/>

														<div className={css.passengerNumber}>
															{passengers[passengerId].number}
														</div>
														<div className={css.passengerName}>
															{passengers[passengerId].fullName}
														</div>
														<Money
															moneyClassName={css.passengerPrice}
															money={service.price}
														/>
													</div>
												);
											});
										})}
									</div>
								);
							})}
						</React.Fragment>
					);
				})}
			</div>
		</ModalX>
	);
};

export default TransferSelectorModal;
